import { Component, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  FeCorridorSystemsService,
  CorridorSystem,
} from '../../services/fe-corridor-systems.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ErrorHandler } from '../../utils/error-handler';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-fe-corridor-systems',
  templateUrl: './fe-corridor-systems.component.html',
  styleUrls: ['./fe-corridor-systems.component.scss'],
  standalone: false,
})
export class FeCorridorSystemsComponent implements OnInit {
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  corridorSystems: CorridorSystem[] = [
    {
      developmentArea: '',
      facilityType: 'CS',
      facilitiesContained: [],
      pseudoFacilityName: '',
      isEditing: true,
      isNew: true,
    },
  ];

  selectedRows: CorridorSystem[] = [];
  filteredCorridorSystems: CorridorSystem[] = [];
  
  // Loading states
  isSaving: boolean = false;
  isLoading: boolean = true;
  hasNewRows: boolean = false; 
  public dialogMessage: string = '';

  showFloatingFilter: boolean = false;
  activeFilter: string = '';
  dropdownPosition: { top: number; left: number } = { top: 0, left: 0 };
  filterSearch: { [key: string]: string } = {};
  filterColumnsData: { [key: string]: string[] } = {};

  multiFilters: {
    developmentArea: string[];
    facilityType: string[];
    facilitiesContained: string[];
    pseudoFacilityName: string[];
  } = {
    developmentArea: [],
    facilityType: [],
    facilitiesContained: [],
    pseudoFacilityName: [],
  };

  currentFilters: {
    developmentArea: string | null;
    facilityType: string | null;
    facilitiesContained: string | null;
    pseudoFacilityName: string | null;
  } = {
    developmentArea: null,
    facilityType: null,
    facilitiesContained: null,
    pseudoFacilityName: null,
  };

  developmentAreas: string[] = [];
  facilityTypes: string[] = ['CS'];
  facilitiesContainedOptions: string[] = [];

  facilitiesByDevArea: { [devArea: string]: string[] } = {};

  constructor(
    private corridorSystemsService: FeCorridorSystemsService,
    private toastr: ToastrService,
    private authService: AuthService,
    private dialog: MatDialog
  ) {
    this.filteredCorridorSystems = [...this.corridorSystems];
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadDevelopmentAreas();
      await this.loadFilterOptionsInit();
      await this.loadCorridorSystems();
    } finally {
      this.isLoading = false;
    }
  }

  private hasAppliedFilters(): boolean {
    return Object.values(this.currentFilters).some((value) => value !== null);
  }

  private buildFilterRequest(): {
    developmentArea: string | null;
    facilityType: string | null;
    facilityNames: string | null;
    pseudoFacilityName: string | null;
  } {
    return {
      developmentArea: this.currentFilters.developmentArea,
      facilityType: this.currentFilters.facilityType,
      facilityNames: this.currentFilters.facilitiesContained,
      pseudoFacilityName: this.currentFilters.pseudoFacilityName,
    };
  }

  private setFilterColumnsData(filterData: {
    developmentArea: string[];
    facilityType: string[];
    facilitiesContained: string[];
    pseudoFacilityName: string[];
  }): void {
    this.filterColumnsData['developmentArea'] = [...(filterData.developmentArea || [])];
    this.filterColumnsData['facilityType'] = [...(filterData.facilityType || [])];
    this.filterColumnsData['facilitiesContained'] = [...(filterData.facilitiesContained || [])];
    this.filterColumnsData['pseudoFacilityName'] = [...(filterData.pseudoFacilityName || [])];
  }

  async loadFilterOptionsInit(): Promise<void> {
    try {
      const filterData = await this.corridorSystemsService.getFilterOptionsBySelection({
        developmentArea: null,
        facilityType: null,
        facilityNames: null,
        pseudoFacilityName: null,
      });

      this.setFilterColumnsData(filterData);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filter options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async reloadFilterOptions(): Promise<void> {
    try {
      const filterData = await this.corridorSystemsService.getFilterOptionsBySelection(
        this.buildFilterRequest()
      );

      this.setFilterColumnsData(filterData);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reload filter options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async loadDevelopmentAreas(): Promise<void> {
    try {
      this.developmentAreas =
        await this.corridorSystemsService.getDevelopmentAreas();
    } catch (error) {
      this.toastr.error('Failed to load development areas', 'Error');
    }
  }

  async loadCorridorSystems(): Promise<void> {
    try {
      const systems = await this.corridorSystemsService.getCorridorSystems(
        this.buildFilterRequest()
      );

      if (systems && systems.length > 0) {
        this.corridorSystems = systems.map((system) => ({
          ...system,
          isEditing: false,
          isNew: false,
        }));

        const uniqueDevAreas = [
          ...new Set(systems.map((s) => s.developmentArea).filter(Boolean)),
        ];

        uniqueDevAreas.forEach(devArea => {
          if (!this.developmentAreas.includes(devArea)) {
            this.developmentAreas.push(devArea);
          }
        });
        this.developmentAreas.sort();
        this.applyAllFilters();
      } else {
        this.corridorSystems = this.hasAppliedFilters()
          ? []
          : [
              {
                developmentArea: '',
                facilityType: 'CS',
                facilitiesContained: [],
                pseudoFacilityName: '',
                isEditing: true,
                isNew: true,
              },
            ];
        this.applyAllFilters();
        if (!this.hasAppliedFilters()) {
          this.toastr.info('No existing corridor systems found', 'Info');
        }
      }
    } catch (error) {
      this.toastr.error('Failed to load corridor systems', 'Error');
    }
  }

  async onDevelopmentAreaChange(system: CorridorSystem): Promise<void> {
    if (!system.isNew) {
      return;
    }

    this.markAsEdited(system);

    if (!system.facilityType) {
      system.facilityType = 'CS';
    }

    system.facilitiesContained = [];
    system.pseudoFacilityName = '';

    if (system.developmentArea) {
      if (!this.facilitiesByDevArea[system.developmentArea]) {
        await this.loadFacilitiesForDevArea(system.developmentArea);
      }
    }
  }

  async loadFacilitiesForDevArea(devArea: string): Promise<void> {
    try {
      const facilities = await this.corridorSystemsService.getFacilitiesByDevArea(devArea);
      this.facilitiesByDevArea[devArea] = facilities;
    } catch (error) {
      this.facilitiesByDevArea[devArea] = [];
      this.toastr.error(`Failed to load facilities for ${devArea}`, 'Error');
    }
  }

  getFacilitiesForDevArea(devArea: string): string[] {
    return this.facilitiesByDevArea[devArea] || [];
  }

  async onFacilitiesDropdownFocus(system: CorridorSystem): Promise<void> {
    this.markAsEdited(system);
    if (!system.developmentArea) {
      return;
    }

    await this.loadFacilitiesForDevArea(system.developmentArea);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (
      !target.closest('.floating-filter-dropdown') &&
      !target.closest('.filter-icon')
    ) {
      this.showFloatingFilter = false;
    }
  }

  openFloatingFilter(event: MouseEvent, field: string): void {
    event.stopPropagation();
    this.activeFilter = field;
    this.showFloatingFilter = true;

    const rect = (event.target as HTMLElement).getBoundingClientRect();

    this.dropdownPosition = {
      top: rect.bottom + 5,
      left: Math.max(10, rect.left - 100),
    };

    const dropdownWidth = 220;
    const viewportWidth = window.innerWidth;

    if (this.dropdownPosition.left + dropdownWidth > viewportWidth) {
      this.dropdownPosition.left = viewportWidth - dropdownWidth - 10;
    }

    const dropdownHeight = 200;
    const viewportHeight = window.innerHeight;

    if (this.dropdownPosition.top + dropdownHeight > viewportHeight) {
      this.dropdownPosition.top = rect.top - dropdownHeight - 5;
    }
  }

  getFilterOptions(field: string): string[] {
    if (this.filterColumnsData[field]) {
      const searchTerm = this.filterSearch[field]?.toLowerCase() || '';
      return (this.filterColumnsData[field] || [])
        .filter((option) => option.toLowerCase().includes(searchTerm))
        .sort();
    }

    const searchTerm = this.filterSearch[field]?.toLowerCase() || '';
    let options: string[] = [];

    switch (field) {
      case 'developmentArea':
        options = [
          ...new Set(
            this.corridorSystems.map((s) => s.developmentArea).filter(Boolean)
          ),
        ];
        break;
      case 'facilityType':
        options = [
          ...new Set(
            this.corridorSystems.map((s) => s.facilityType).filter(Boolean)
          ),
        ];
        break;
      case 'facilitiesContained':
        options = [
          ...new Set(
            this.corridorSystems.flatMap((s) => s.facilitiesContained)
          ),
        ];
        break;
      case 'pseudoFacilityName':
        options = [
          ...new Set(
            this.corridorSystems
              .map((s) => s.pseudoFacilityName)
              .filter(Boolean)
          ),
        ];
        break;
    }

    return options
      .filter((option) => option.toLowerCase().includes(searchTerm))
      .sort();
  }

  isChecked(field: string, option: string): boolean {
    return (
      this.multiFilters[field as keyof typeof this.multiFilters]?.includes(
        option
      ) || false
    );
  }

  isFilterActive(field: string): boolean {
    const values = this.multiFilters[field as keyof typeof this.multiFilters];
    return Array.isArray(values) && values.length > 0;
  }

  toggleMultiFilter(field: string, option: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const filterKey = field as keyof typeof this.multiFilters;

    if (!this.multiFilters[filterKey]) {
      this.multiFilters[filterKey] = [];
    }

    if (isChecked) {
      if (!this.multiFilters[filterKey].includes(option)) {
        this.multiFilters[filterKey].push(option);
      }
    } else {
      this.multiFilters[filterKey] = this.multiFilters[filterKey].filter(
        (item) => item !== option
      );
    }
  }

  async applyFilter(field: string): Promise<void> {
    const selectedValues = this.multiFilters[field as keyof typeof this.multiFilters] || [];

    this.currentFilters[field as keyof typeof this.currentFilters] =
      selectedValues.length > 0 ? selectedValues.join(',') : null;

    await this.loadCorridorSystems();
    await this.reloadFilterOptions();
    this.applyAllFilters();
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  async clearFilter(field: string): Promise<void> {
    const filterKey = field as keyof typeof this.multiFilters;
    this.multiFilters[filterKey] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field as keyof typeof this.currentFilters] = null;

    await this.loadCorridorSystems();
    await this.reloadFilterOptions();
    this.applyAllFilters();
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  private applyAllFilters(): void {
    this.filteredCorridorSystems = this.corridorSystems.filter((system) => {
      if (this.multiFilters.developmentArea?.length > 0) {
        if (
          !this.multiFilters.developmentArea.includes(system.developmentArea)
        ) {
          return false;
        }
      }

      if (this.multiFilters.facilityType?.length > 0) {
        if (!this.multiFilters.facilityType.includes(system.facilityType)) {
          return false;
        }
      }

      if (this.multiFilters.facilitiesContained?.length > 0) {
        const hasMatch = this.multiFilters.facilitiesContained.some((filter) =>
          system.facilitiesContained.includes(filter)
        );
        if (!hasMatch) {
          return false;
        }
      }

      if (this.multiFilters.pseudoFacilityName?.length > 0) {
        if (
          !this.multiFilters.pseudoFacilityName.includes(
            system.pseudoFacilityName
          )
        ) {
          return false;
        }
      }

      return true;
    });
  }

  getOrderedData(): CorridorSystem[] {
    // Put new rows first, then existing rows
    const newRows = this.filteredCorridorSystems.filter(system => system.isNew);
    const existingRows = this.filteredCorridorSystems.filter(system => !system.isNew);
    return [...newRows, ...existingRows];
  }

  addNewRow(): void {
    const newSystem: CorridorSystem = {
      developmentArea: '',
      facilityType: 'CS',
      facilitiesContained: [],
      pseudoFacilityName: '',
      isEditing: true,
      isNew: true,
    };

    // Add to the beginning of the array so it appears first
    this.corridorSystems.unshift(newSystem);
    this.hasNewRows = true; // Set flag when user clicks New
    this.applyAllFilters();
  }

  async saveRows(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    const errorMessages: string[] = [];

    const changedRows = this.corridorSystems.filter(
      (system) => system.isNew || this.rowHasChanged(system)
    );

    if (changedRows.length === 0) {
      this.toastr.info('No changes to save.');
      return;
    }

    // Start loading spinner
    this.isSaving = true;

    try {
      for (const system of changedRows) {
        let saveResponse: any;

        if (system.isNew) {
          saveResponse = await this.corridorSystemsService.saveCorridorSystem(
            system,
            'INSERT',
            userName
          );
        } else {
          const oldPseudoFacilityName =
            system.originalData?.pseudoFacilityName ??
            system.pseudoFacilityName;
          saveResponse = await this.corridorSystemsService.saveCorridorSystem(
            system,
            'UPDATE',
            userName,
            oldPseudoFacilityName
          );
        }

        const parsed = ErrorHandler.parseApiResult(saveResponse);

        if (!parsed.success) {
          failureCount++;
          errorMessages.push(parsed.message || 'Failed to save corridor system.');
          continue;
        }

        system.isNew = false;
        system.isEditing = false;
        system.originalData = {
          ...system,
          facilitiesContained: [...system.facilitiesContained],
        };
        successCount++;
      }

      // Only reload corridor systems after saving - development areas are already loaded
      await this.loadCorridorSystems();
      await this.reloadFilterOptions();

      if (successCount > 0) {
        this.toastr.success(
          `${successCount} corridor system(s) saved successfully`,
          'Success'
        );
      }

      if (failureCount > 0) {
        const detailedErrorMsg =
          errorMessages.length > 0
            ? errorMessages.join('<br>')
            : `${failureCount} corridor system(s) failed to save`;

        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true,
        });
      }
    } finally {
      // Stop loading spinner regardless of success/failure
      this.isSaving = false;
    }
  }

  cancelEdit(): void {
    const changedRows = this.corridorSystems.filter(
      (system) => system.isNew || system.isEditing
    );

    changedRows.forEach((system, index) => {
      if (system.originalData) {
        Object.assign(system, system.originalData);
        delete system.originalData;
        system.isEditing = false;
      } else if (system.isNew) {
        const systemIndex = this.corridorSystems.indexOf(system);
        if (systemIndex > -1) {
          this.corridorSystems.splice(systemIndex, 1);
        }
      }
    });

    // Reset hasNewRows flag when all new rows are removed
    this.hasNewRows = this.corridorSystems.some(system => system.isNew);
    this.applyAllFilters();
  }
  clearSelection(): void {
    if (!this.selectedRows.length) {
      return;
    }
    this.selectedRows = [];
    this.toastr.info('Selection cleared.', 'Info');
  }

  canClearSelection(): boolean {
    return this.selectedRows.length > 0;
  }

  async deleteSelectedCorridors(): Promise<void> {
    const userName = this.authService.getUserName();

    const names = Array.from(
      new Set(
        this.selectedRows
          .map((r) => (r.pseudoFacilityName || '').trim())
          .filter((n) => !!n)
      )
    );

    if (names.length === 0) {
      this.toastr.info(
        'Select at least one corridor system with a pseudo facility name to delete.',
        'Info'
      );
      return;
    }

    this.dialogMessage =
      names.length === 1
        ? `Delete corridor system "${names[0]}"?`
        : `Delete ${names.length} corridor systems?\n${names.join(', ')}`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    let successCount = 0;
    let failureCount = 0;
    const deleteErrorMessages: string[] = [];

    for (const name of names) {
      const response = await this.corridorSystemsService.deleteCorridorSystem(name, userName);
      const parsed = ErrorHandler.parseApiResult(response);

      if (parsed.success) {
        successCount++;
      } else {
        failureCount++;
        deleteErrorMessages.push(
          parsed.message || `Failed to delete corridor system "${name}".`
        );
      }
    }

    if (successCount > 0) {
      this.corridorSystems = this.corridorSystems.filter(
        (r) => !names.includes((r.pseudoFacilityName || '').trim())
      );
      this.selectedRows = this.selectedRows.filter(
        (r) => !names.includes((r.pseudoFacilityName || '').trim())
      );
    }

    if (this.corridorSystems.length === 0) {
      this.corridorSystems.push({
        developmentArea: '',
        facilityType: 'CS',
        facilitiesContained: [],
        pseudoFacilityName: '',
        isEditing: true,
        isNew: true,
      });
    }

    await this.loadCorridorSystems();
    await this.reloadFilterOptions();

    if (successCount) {
      this.toastr.success(
        `${successCount} corridor system${
          successCount > 1 ? 's' : ''
        } deleted.`,
        'Deleted'
      );
    }
    if (failureCount) {
      this.toastr.error(deleteErrorMessages.join('<br>'), 'Delete Error', {
        timeOut: 8000,
        enableHtml: true,
      });
    }
  }

  toggleRowSelection(row: CorridorSystem, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      if (!this.selectedRows.includes(row)) {
        this.selectedRows.push(row);
      }
    } else {
      this.selectedRows = this.selectedRows.filter((r) => r !== row);
    }
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {
      developmentArea: [],
      facilityType: [],
      facilitiesContained: [],
      pseudoFacilityName: [],
    };
    this.currentFilters = {
      developmentArea: null,
      facilityType: null,
      facilitiesContained: null,
      pseudoFacilityName: null,
    };
    this.filterSearch = {};

    await this.loadCorridorSystems();
    await this.loadFilterOptionsInit();
    this.applyAllFilters();
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  hasActiveFilters(): boolean {
    return (
      Object.values(this.multiFilters).some((arr) => arr.length > 0) ||
      Object.values(this.filterSearch).some((val) => val && val.length > 0)
    );
  }

  hasEditingRows(): boolean {
    return this.corridorSystems.some((system) => system.isEditing);
  }

  addFacility(system: CorridorSystem, event: Event): void {
    this.markAsEdited(system);

    const selected = (event.target as HTMLSelectElement).value;
    if (selected && !system.facilitiesContained.includes(selected)) {
      system.facilitiesContained.push(selected);
    }
    (event.target as HTMLSelectElement).value = '';
  }

  removeFacility(system: CorridorSystem, facility: string): void {
    this.markAsEdited(system);

    system.facilitiesContained = system.facilitiesContained.filter(
      (f) => f !== facility
    );
  }
  rowHasChanged(system: CorridorSystem): boolean {
    if (!system.originalData) return false;
    return (
      system.developmentArea !== system.originalData.developmentArea ||
      system.facilityType !== system.originalData.facilityType ||
      system.pseudoFacilityName !== system.originalData.pseudoFacilityName ||
      system.facilitiesContained.join(',') !==
        system.originalData.facilitiesContained.join(',')
    );
  }

  markAsEdited(system: CorridorSystem): void {
    if (!system.originalData) {
      system.originalData = {
        ...system,
        facilitiesContained: [...system.facilitiesContained],
      };
    }
    system.isEditing = true;
  }

  async exportToCSV(): Promise<void> {
    const exportData = this.filteredCorridorSystems.length
      ? this.filteredCorridorSystems
      : this.corridorSystems;

    if (!exportData.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = exportData.map((system) => ({
      'Development Area(s)': system.developmentArea || '',
      'Facility Type': system.facilityType || '',
      'Facilities Contained in Node': (system.facilitiesContained || []).join(', '),
      'Pseudo Facility Name': system.pseudoFacilityName || '',
    }));

    await exportRowsToExcel(
      worksheetData,
      [
        'Development Area(s)',
        'Facility Type',
        'Facilities Contained in Node',
        'Pseudo Facility Name',
      ],
      'fe-corridor-systems.xlsx',
      'FECorridorSystems'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.corridorSystemsService.exportAllCorridorSystemsData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download  export.');
    }
  }
}
